import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  LayoutDashboard,
  Calendar,
  Stethoscope,
  Settings,
  Users,
  X,
  LogOut,
  Bell,
  Wallet,
  Receipt,
  ClipboardList,
  MessageSquareText,
  Menu,
} from "lucide-react";

import AppointmentsSection from "./AppointmentSection";
import DoctorSection from "./DoctorSection";
import AdminSettings from "./AdminSettings";
import RemindersSection from "./ReminderSection";
import JobManagement from "./JobManagement";
import Dashboard from "./Dashboard";
import Header from "../Header";
import AdminTodayQueue from "./AdminTodayQueue";
import PatientPage from "./PatientPage";
import PaymentsSection from "./PaymentsSection";
import ExpensesSection from "./ExpensesSection";

import PatientRegistration from "../../vpjc-patient-forms/pages/PatientRegistration";
import PatientFeedback from "../../vpjc-patient-forms/pages/PatientFeedback";

import { collection, getDocs, onSnapshot } from "firebase/firestore";
import { auth, db } from "../../firebase";
import PatientRegistrationDashboard from "../../vpjc-patient-forms/pages/PatientRegistrationDashboard";
import PatientFeedbackDashboard from "../../vpjc-patient-forms/pages/PatientFeedbackDashboard";

const navItems = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "appointments", label: "Appointments", icon: Calendar },
  { key: "tokens", label: "Tokens", icon: Bell },
  { key: "doctors", label: "Doctors", icon: Stethoscope },
  { key: "patients", label: "Patients", icon: Users },
  {
    key: "patientRegistration",
    label: "Patient Registration",
    icon: ClipboardList,
  },
  {
    key: "patientFeedback",
    label: "Patient Feedback",
    icon: MessageSquareText,
  },
  { key: "reminders", label: "Reminders", icon: Bell },
  { key: "jobManagement", label: "Job Management", icon: LayoutDashboard },
  { key: "payments", label: "Payments", icon: Wallet },
  { key: "expenses", label: "Expenses", icon: Receipt },
  { key: "settings", label: "Settings", icon: Settings },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [isOpen, setIsOpen] = useState(
    typeof window !== "undefined" && window.innerWidth >= 768
  );
  const [activeTab, setActiveTab] = useState("dashboard");
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const [appointments, setAppointments] = useState([]);
  const [quickConsultations, setQuickConsultations] = useState([]);
  const [tokensData, setTokensData] = useState([]);
  const [patients, setPatients] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [packages, setPackages] = useState([]);
  const [packagePayments, setPackagePayments] = useState([]);
  const [callbackRequests, setCallbackRequests] = useState([]);

  const [admin, setAdmin] = useState({ name: "", role: "Admin" });
  const [selectedVPJC, setSelectedVPJC] = useState(null);

  const tabFromUrl = searchParams.get("tab");
  const vpjcFromUrl = searchParams.get("vpjcId");

  // Load patients
  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const snap = await getDocs(collection(db, "patients"));

        const list = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setPatients(list);
      } catch (error) {
        console.error("Error fetching patients:", error);
      }
    };

    fetchPatients();
  }, []);

  // Load admin details
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user");

      if (storedUser) {
        const parsed = JSON.parse(storedUser);

        setAdmin({
          name: parsed.name || "Admin",
          role: parsed.role || "Admin",
        });
      }
    } catch (error) {
      console.error("Unable to load admin details:", error);
    }
  }, []);

  // Read active tab from URL
  useEffect(() => {
    const validTabs = navItems.map((item) => item.key);

    if (tabFromUrl && validTabs.includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    } else if (!tabFromUrl) {
      setActiveTab("dashboard");
    }
  }, [tabFromUrl]);

  // Read selected patient from URL
  useEffect(() => {
    if (vpjcFromUrl) {
      setSelectedVPJC(vpjcFromUrl);
      setActiveTab("patients");
    } else {
      setSelectedVPJC(null);
    }
  }, [vpjcFromUrl]);

  // Sidebar responsive behavior
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsOpen(false);
      } else {
        setIsOpen(true);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch appointments, quick consultations and callback requests
  const fetchAppointments = async () => {
    try {
      const snapshot = await getDocs(collection(db, "appointments"));

      const appointmentsData = snapshot.docs.map((doc) => {
        const data = doc.data();

        return {
          id: doc.id,
          bookingId: data.bookingId || doc.id,
          name: data.name || "N/A",
          email: data.email || "N/A",
          phone: data.phone || "N/A",
          date: data.date || "N/A",
          time: data.time || "N/A",
          service: data.service || "N/A",
          status: data.status || "Pending",
          consultationType: data.consultationType || "Walk-in",
          branch: data.branch || "",
          pincode: data.pincode || "",
          locality: data.locality || "",
          flatNo: data.flatNo || "",
          landmark: data.landmark || "",
          message: data.message || "N/A",
          cancelReason: data.cancelReason || "",
          createdAt: data.timestamp
            ? data.timestamp.toDate()
            : new Date(0),
        };
      });

      appointmentsData.sort((a, b) => b.createdAt - a.createdAt);
      setAppointments(appointmentsData);

      const quickSnap = await getDocs(
        collection(db, "quickConsultations")
      );

      const quickData = quickSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().timestamp
          ? doc.data().timestamp.toDate()
          : new Date(0),
      }));

      quickData.sort((a, b) => b.createdAt - a.createdAt);
      setQuickConsultations(quickData);

      const cbSnap = await getDocs(
        collection(db, "callbackRequests")
      );

      const cbData = cbSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().timestamp
          ? doc.data().timestamp.toDate()
          : new Date(0),
      }));

      cbData.sort((a, b) => b.createdAt - a.createdAt);
      setCallbackRequests(cbData);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, []);

  // Realtime visits
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "visits"),
      (snapshot) => {
        const visitsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          visit_date: doc.data().visit_date || "",
          consultation_payment_status:
            doc.data().consultation_payment_status || "UNPAID",
          consultation_fee: doc.data().consultation_fee || 0,
          created_at: doc.data().created_at || null,
        }));

        setTokensData(visitsData);
      },
      (error) => {
        console.error("Error listening to visits:", error);
      }
    );

    return () => unsub();
  }, []);

  // Realtime treatment entries
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "treatment_entries"),
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setTreatments(data);
      },
      (error) => {
        console.error("Error listening to treatments:", error);
      }
    );

    return () => unsub();
  }, []);

  // Realtime package payments
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "package_payments"),
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setPackagePayments(data);
      },
      (error) => {
        console.error("Error listening to package payments:", error);
      }
    );

    return () => unsub();
  }, []);

  // Realtime packages
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "packages"),
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setPackages(data);
      },
      (error) => {
        console.error("Error listening to packages:", error);
      }
    );

    return () => unsub();
  }, []);

  const getInitials = (name) =>
    name
      ? name
          .split(" ")
          .map((part) => part[0])
          .join("")
          .toUpperCase()
      : "A";

  const handleNavigation = (key) => {
    if (key === "patients") {
      setSelectedVPJC(null);
      navigate("/admin-dashboard?tab=patients");
    } else {
      navigate(`/admin-dashboard?tab=${key}`);
    }

    setActiveTab(key);

    // Close the sidebar on small screens after navigation
    if (window.innerWidth < 768) {
      setIsOpen(false);
    }
  };

  const handleLogout = async () => {
    try {
      localStorage.removeItem("user");
      await auth.signOut();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  return (
    <>
      <Header hideBookButton={true} />

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-16 z-40 flex h-[calc(100%-4rem)] flex-col border-r bg-white shadow-sm transition-all duration-300 ${
          isOpen ? "w-64" : "w-16 md:w-20"
        }`}
      >
        {/* Admin profile and sidebar toggle */}
        <div className="flex flex-col items-center border-b py-2">
          <div
            className={`flex w-full items-center ${
              isOpen ? "justify-end px-3" : "justify-center"
            }`}
          >
            <button
              type="button"
              onClick={() => setIsOpen((previous) => !previous)}
              aria-label={isOpen ? "Collapse sidebar" : "Expand sidebar"}
              title={isOpen ? "Collapse sidebar" : "Expand sidebar"}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-blue-700 hover:bg-blue-50"
            >
              {isOpen ? <X size={20} /> : <Menu size={21} />}
            </button>
          </div>

          {isOpen && (
            <div className="mt-1 flex flex-col items-center px-2 pb-3">
              <div className="mb-1 flex h-14 w-14 items-center justify-center rounded-full border-2 border-blue-600 bg-blue-100 text-xl font-bold text-blue-700">
                {getInitials(admin.name)}
              </div>

              <h2 className="max-w-full truncate text-center text-base font-semibold text-blue-700">
                {admin.name}
              </h2>

              <p className="text-sm capitalize text-gray-500">
                {admin.role || "Admin"}
              </p>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex flex-1 flex-col justify-between overflow-y-auto p-2">
          <div className="flex flex-col gap-1.5">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.key;

              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => handleNavigation(item.key)}
                  title={!isOpen ? item.label : undefined}
                  aria-label={item.label}
                  className={`flex min-h-10 w-full items-center rounded-xl px-2 py-2 text-left text-sm transition-colors ${
                    isOpen ? "gap-3" : "justify-center"
                  } ${
                    isActive
                      ? "bg-blue-100 font-semibold text-blue-700"
                      : "text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <Icon size={20} className="shrink-0" />

                  {isOpen && (
                    <span className="min-w-0 truncate">{item.label}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Logout */}
          <button
            type="button"
            onClick={() => setShowLogoutConfirm(true)}
            title={!isOpen ? "Logout" : undefined}
            className={`mt-4 flex min-h-10 w-full items-center rounded-xl px-2 py-2 text-sm text-red-600 transition-colors hover:bg-red-50 ${
              isOpen ? "gap-3" : "justify-center"
            }`}
          >
            <LogOut size={20} className="shrink-0" />
            {isOpen && <span>Logout</span>}
          </button>
        </nav>

        {/* Logout confirmation */}
        <Dialog
          open={showLogoutConfirm}
          onOpenChange={setShowLogoutConfirm}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Confirm Logout</DialogTitle>
              <DialogDescription>
                Are you sure you want to log out?
              </DialogDescription>
            </DialogHeader>

            <DialogFooter className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => setShowLogoutConfirm(false)}
              >
                Cancel
              </Button>

              <Button
                className="bg-red-500 text-white hover:bg-red-600"
                onClick={handleLogout}
              >
                Logout
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </aside>

      {/* Main content */}
      <main
        className={`min-h-screen px-3 pb-8 pt-24 transition-all duration-300 sm:px-6 lg:px-8 ${
          isOpen ? "ml-16 sm:ml-64" : "ml-16 sm:ml-20"
        }`}
      >
        {activeTab === "dashboard" && (
          <Dashboard
            appointments={appointments}
            tokens={tokensData}
            patients={patients}
            treatments={treatments}
            packagePayments={packagePayments}
            packages={packages}
          />
        )}

        {activeTab === "appointments" && (
          <AppointmentsSection
            appointments={appointments}
            quickConsultations={quickConsultations}
            callbackRequests={callbackRequests}
            fetchAppointments={fetchAppointments}
          />
        )}

        {activeTab === "tokens" && (
          <AdminTodayQueue
            onSelectPatient={(vpjcId) => {
              navigate(
                `/admin-dashboard?tab=patients&vpjcId=${encodeURIComponent(
                  vpjcId
                )}`
              );
            }}
          />
        )}

        {activeTab === "doctors" && <DoctorSection />}

        {activeTab === "patients" && (
          <PatientPage
            key={selectedVPJC || "empty"}
            searchVPJC={selectedVPJC}
          />
        )}

        {/* New: Patient Registration */}
       {activeTab === "patientRegistration" && (
          <PatientRegistrationDashboard />
        )}

        {/* New: Patient Feedback */}
        {activeTab === "patientFeedback" && (
          <PatientFeedbackDashboard />
        )}

        {activeTab === "reminders" && <RemindersSection db={db} />}

        {activeTab === "jobManagement" && <JobManagement />}

        {activeTab === "payments" && <PaymentsSection />}

        {activeTab === "expenses" && <ExpensesSection />}

        {activeTab === "settings" && <AdminSettings />}
      </main>
    </>
  );
}